
import { useState } from "react";
import { useTrialManagement } from "@/hooks/subscription/useTrialManagement";
import { useCheckoutSession } from "@/hooks/subscription/useCheckoutSession";
import { useBillingPortal } from "@/hooks/subscription/useBillingPortal";
import { useAuthActions } from "@/hooks/subscription/useAuthActions";
import { usePlanConfigs } from "@/hooks/subscription/usePlanConfigs";
import { SubscriptionPlanType } from "./utils";

export const useSubscription = (userId: string, userEmail?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const { handleStartFreeTrialWithoutCard } = useTrialManagement(userId);
  const { createCheckoutSession, isLoading: isCheckoutLoading } = useCheckoutSession();
  const { handleManageSubscription, isLoading: isPortalLoading } = useBillingPortal(userId);
  const { handleSignOut, isLoading: isSignOutLoading } = useAuthActions();
  const { data: planConfigs, isLoading: isPlansLoading, error: plansError } = usePlanConfigs();

  // Enhanced debug logging
  console.log(`[${new Date().toISOString()}] [useSubscription] hook - planConfigs:`, planConfigs);
  console.log(`[${new Date().toISOString()}] [useSubscription] hook - isPlansLoading:`, isPlansLoading);
  console.log(`[${new Date().toISOString()}] [useSubscription] hook - plansError:`, plansError);

  // Check if planConfigs is an array and log its structure
  if (planConfigs) {
    console.log(`[${new Date().toISOString()}] [useSubscription] planConfigs is ${Array.isArray(planConfigs) ? 'an array' : 'NOT an array'}`);
    console.log(`[${new Date().toISOString()}] [useSubscription] planConfigs length:`, Array.isArray(planConfigs) ? planConfigs.length : 'N/A');
    
    if (Array.isArray(planConfigs)) {
      planConfigs.forEach((config, index) => {
        console.log(`[${new Date().toISOString()}] [useSubscription] Plan ${index + 1}:`, config);
        console.log(`[${new Date().toISOString()}] [useSubscription] Plan ${index + 1} features:`, config.features);
        console.log(`[${new Date().toISOString()}] [useSubscription] Features is ${Array.isArray(config.features) ? 'an array' : 'NOT an array'}`);
      });
    }
  }

  // Define the wrapper function to match the expected usage
  const handleCreateCheckoutSession = async (
    planName: SubscriptionPlanType, 
    currentPlan?: string,
    isAnnual: boolean = false
  ) => {
    try {
      // Only proceed if not already loading
      if (isLoading) return { success: false, error: "Already processing a request" };
      
      setIsLoading(true);
      console.log(`[${new Date().toISOString()}] [useSubscription] Initiating checkout session creation:`, { 
        userId, 
        userEmail, 
        planName, 
        currentPlan,
        isAnnual
      });
      
      const result = await createCheckoutSession(
        userId, 
        planName, 
        userEmail, 
        currentPlan,
        isAnnual
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to create checkout session');
      }
      
      return result;
    } catch (error) {
      console.error(`[${new Date().toISOString()}] [useSubscription] Error in handleCreateCheckoutSession:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Combine loading states
  const combinedLoading = isLoading || isCheckoutLoading || isPortalLoading || isSignOutLoading;

  return {
    isLoading: combinedLoading,
    isPlansLoading,
    plansError,
    planConfigs,
    handleStartFreeTrialWithoutCard,
    handleSignOut,
    handleManageSubscription,
    handleCreateCheckoutSession
  };
};
